import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getData } from 'country-list';
import { getCountryCallingCode, Metadata } from 'libphonenumber-js/min';

// APIs
import { createOrganization } from '../../../Api/CreateOrganization';
import { GetOrganization } from '../../../Api/GetOrganization';
import { AdminOrganizationAccount } from '../../../Api/AdminOrganizationAccount';
import { userListByOrganization } from '../../../Api/UserListbyOrganization';
import { GetProjectData } from '../../../Api/Project';
import { SwitchAccount } from '../../../Api/switchAccount';
import { hasTechAdminAccess } from '../../../Utils/adminUserTypes';
import { AuthContext } from '../../../Layout/AuthContext';
import OrganizationLookup from '../../OrganizationLookup/OrganizationLookup';

const DialogOrganizationAdd = ({ open, onClose, context }) => {
  const navigate = useNavigate();
  const { switchAccount } = useContext(AuthContext);
  const userData = JSON.parse(localStorage.getItem('userLogindata'));
  const accountId = localStorage.getItem('accountId');

  // State Management
  // A Zavvy card may suggest the name and country; then the dialog opens on Basic Details.
  const prefill = context?.prefillValues || {};
  const [step, setStep] = useState(prefill.organization_name ? 2 : 1); // 1: Review, 2: Basic Details, 3: Contact & Size, 4: Review & Create
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [orgUserCounts, setOrgUserCounts] = useState({});
  const [orgProjectCounts, setOrgProjectCounts] = useState({});
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    organization_name: '',
    entity_name: '',
    incorporation_number: '',
    country: 'Singapore',
    address: '',
    phone_number: '',
    country_code: '+65',
    website: '',
    employee_estimated: '',
    annual_turnover: '',
    is_consultant: false,
    ...(prefill.organization_name ? { organization_name: prefill.organization_name } : {}),
    ...(prefill.country ? { country: prefill.country } : {}),
  });
  
  const [errors, setErrors] = useState({});
  const [continueToOnboarding, setContinueToOnboarding] = useState(context?.continueToOnboarding ?? true);

  // Country data
  const countriesName = getData();
  const allCountries = new Metadata().getCountries();
  const countryCodes = allCountries.map((country) => {
    const callingCode = getCountryCallingCode(country);
    return {
      code: country,
      callingCode: `+${callingCode}`,
      label: `${country} (+${callingCode})`,
    };
  });

  // Move Singapore to top
  const singaporeIndex = countriesName.findIndex(country => country.code === 'SG');
  if (singaporeIndex > -1) {
    const singapore = countriesName.splice(singaporeIndex, 1)[0];
    countriesName.unshift(singapore);
  }

  // Employee size options
  const employeeSizeOptions = [
    { value: '<10', label: '< 10' },
    { value: '11-20', label: '11-20' },
    { value: '21-50', label: '21-50' },
    { value: '51-99', label: '51-99' },
    { value: '100-200', label: '100-200' },
    { value: '>200', label: '> 200' }
  ];

  // Annual turnover options
  const annualTurnoverOptions = [
    { value: 'Micro Enterprise (<$1mil)', label: 'Micro Enterprise (<$1mil)' },
    { value: 'Small Enterprise ($1mil-S$10mil)', label: 'Small Enterprise ($1mil-$10mil)' },
    { value: 'Medium Enterprise (S$10mil-S$100mil)', label: 'Medium Enterprise ($10mil-$100mil)' },
    { value: 'Large Enterprise (>S$100mil)', label: 'Large Enterprise (>$100mil)' }
  ];

  // Fetch organizations on mount
  useEffect(() => {
    if (open && step === 1) {
      fetchOrganizations();
    }
  }, [open, step]);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await GetOrganization(userData?.ID || userData?.user?.user_id);
      if (response?.data) {
        // Enhance with logo URLs
        const enhancedOrgs = await enhanceOrganizationsWithLogos(response.data);
        setOrganizations(enhancedOrgs);
        // Fetch user counts for each organization
        fetchUserCounts(enhancedOrgs);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const enhanceOrganizationsWithLogos = async (organizations) => {
    if (!Array.isArray(organizations) || organizations.length === 0) return organizations;
    
    // Import FileUploadGet if available
    try {
      const { FileUploadGet } = await import('../../../Api/FileUpload');
      
      const enhancedOrgs = await Promise.all(organizations.map(async (org) => {
        // Skip if org already has logo URL
        if (org.logo1_sas_url || org.logo1_file_url) {
          return org;
        }
        
        // If org has logo1 ID but no URL, try to fetch it
        if (org.logo1) {
          try {
            const response = await FileUploadGet(org.logo1);
            if (response?.data?.data?.sas_url) {
              return { 
                ...org, 
                logo1_sas_url: response.data.data.sas_url 
              };
            }
          } catch (error) {
            console.error('Error fetching logo for org:', org.organization_name, error);
          }
        }
        
        return org;
      }));
      
      return enhancedOrgs;
    } catch (error) {
      // FileUploadGet not available, return orgs as-is
      return organizations;
    }
  };

  const fetchUserCounts = async (orgs) => {
    const userCounts = {};
    const projectCounts = {};
    
    await Promise.all(
      orgs.map(async (org) => {
        try {
          // Fetch user count
          const userResponse = await userListByOrganization(org.id);
          userCounts[org.id] = userResponse?.data?.length || 0;
          
          // Fetch project count
          const projectResponse = await GetProjectData(org.id);
          projectCounts[org.id] = projectResponse?.data?.length || 0;
        } catch (error) {
          userCounts[org.id] = 0;
          projectCounts[org.id] = 0;
        }
      })
    );
    
    setOrgUserCounts(userCounts);
    setOrgProjectCounts(projectCounts);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 2) {
      if (!formData.organization_name?.trim()) {
        newErrors.organization_name = 'Organization name is required';
      }
      if (!formData.country?.trim()) {
        newErrors.country = 'Country is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 2 && !validateStep(2)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleAcceptOrganizationFill = (organizationData) => {
    setFormData((prev) => ({
      ...prev,
      ...(organizationData.registerEntity ? { entity_name: organizationData.registerEntity } : {}),
      ...(organizationData.incorporationNumber
        ? { incorporation_number: organizationData.incorporationNumber }
        : {}),
      ...(organizationData.address ? { address: organizationData.address } : {}),
      ...(organizationData.selectedCountry ? { country: organizationData.selectedCountry } : {}),
      ...(organizationData.companyWebsite ? { website: organizationData.companyWebsite } : {}),
    }));
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = {
        organization_name: formData.organization_name,
        entity_name: formData.entity_name || null,
        incorporation_number: formData.incorporation_number || null,
        country: formData.country,
        address: formData.address || null,
        phone_number: formData.phone_number ? `${formData.country_code}-${formData.phone_number}` : null,
        website: formData.website || null,
        employee_estimated: formData.employee_estimated || null,
        annual_turnover: formData.annual_turnover || null,
        is_consultant: formData.is_consultant,
      };
      
      const response = await createOrganization(JSON.stringify(payload));
      
      if (response?.status === 201) {
        const newOrgId = response?.data?.data?.id;
        
        // Always add creator to the organization
        let newAccountId = null;
        try {
          const accountPayload = {
            user_id: userData?.ID || userData?.user?.user_id,
            organization_id: newOrgId,
            profile_id: userData?.RoleID || userData?.user?.role_id,
          };
          const accountResponse = await AdminOrganizationAccount(accountPayload);
          newAccountId = accountResponse?.data?.data?.id;
        } catch (error) {
          console.error('Error adding user to organization:', error);
          toast.error('Failed to add you to the organization, but organization was created');
        }
        
        toast.success('Organization created successfully!');
        
        // Switch to new organization and open onboarding in new tab if checked
        if (continueToOnboarding && newAccountId) {
          try {
            // Switch to the new organization
            const switchRes = await SwitchAccount(newAccountId);
            if (switchRes?.data) {
              // Update auth context and localStorage
              switchAccount(switchRes.data, 0, () => {}, () => {});
              localStorage.setItem('organizationId', newOrgId);
              sessionStorage.setItem('onboardingOrgId', newOrgId);
              
              toast.success(`Switched to new organization - opening onboarding`);
              
              // Open onboarding in new tab
              setTimeout(() => {
                window.open('/onboarding', '_blank');
              }, 500);
            } else {
              throw new Error('Failed to switch to new organization');
            }
          } catch (error) {
            console.error('Error switching to new organization:', error);
            toast.error('Organization created but failed to switch. Please switch manually.');
          }
        }
        
        context?.onSuccess?.({ organization_id: newOrgId, organization_name: formData.organization_name });
        onClose();
      }
    } catch (error) {
      console.error('Create organization error:', error);
      toast.error(error?.response?.data?.error || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep(1);
    setFormData({
      organization_name: '',
      entity_name: '',
      incorporation_number: '',
      country: 'Singapore',
      address: '',
      phone_number: '',
      country_code: '+65',
      website: '',
      employee_estimated: '',
      annual_turnover: '',
      is_consultant: false
    });
    setErrors({});
    setContinueToOnboarding(true);
    onClose();
  };

  const getSubscriptionStatus = (org) => {
    const days = org.subscription_remaining_days || 0;
    const isExpired = org.subscription_status === 'expired';
    const isExpiring = days <= 14 && days > 0;
    
    if (isExpired) {
      return {
        icon: <LockIcon fontSize="small" sx={{ color: '#F5C935' }} />,
        label: 'Expired',
        color: '#F5C935',
        bgColor: '#FFF9E6'
      };
    }
    
    if (days >= 14) {
      return {
        icon: <CheckCircleIcon fontSize="small" sx={{ color: '#67deb6' }} />,
        label: 'Active',
        color: '#67deb6',
        bgColor: '#ebfef8'
      };
    }
    
    return {
      icon: <AccessTimeIcon fontSize="small" sx={{ color: '#84848f' }} />,
      label: `Trial ends in ${days} days`,
      color: '#84848f',
      bgColor: '#f1f1f3'
    };
  };

  // Shared styles
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#E5E7EB'
      },
      '&:hover fieldset': {
        borderColor: '#D1D5DB'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3B82F6',
        borderWidth: 1
      }
    },
    '& .MuiInputBase-input': {
      color: '#111827'
    }
  };

  const labelSx = {
    color: '#374151',
    fontWeight: 500,
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5
  };

  // Render Step 1: Review Existing Organizations
  const renderStep1 = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <BusinessIcon sx={{ color: '#1D3DFC', fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
          Your Organizations
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {loadingOrgs ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            You currently have {organizations.length} organization{organizations.length !== 1 ? 's' : ''}:
          </Typography>
          
          <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 3 }}>
            {organizations.map((org) => {
              const status = getSubscriptionStatus(org);
              const logoUrl = org?.logo1_sas_url || org?.logo1_file_url || null;
              
              return (
                <Card
                  key={org.id}
                  sx={{
                    mb: 2,
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      borderColor: '#1D3DFC'
                    }
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Logo or Initial */}
                      {logoUrl ? (
                        <Box
                          component="img"
                          src={logoUrl}
                          alt={`${org.organization_name} logo`}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '8px',
                            objectFit: 'contain',
                            border: '1px solid #E5E7EB',
                            backgroundColor: '#FFFFFF',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div style="
                                width: 48px;
                                height: 48px;
                                border-radius: 8px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                background-color: #F3F4F6;
                                color: #6B7280;
                                font-size: 20px;
                                font-weight: bold;
                              ">
                                ${org.organization_name.charAt(0).toUpperCase()}
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {org.organization_name.charAt(0).toUpperCase()}
                        </Box>
                      )}
                      
                      {/* Content - Max 2 lines */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: '1.2'
                          }}
                        >
                          {org.organization_name}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          flexWrap: 'nowrap',
                          overflow: 'hidden'
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6B7280',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {org.country || 'No country set'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280', flexShrink: 0 }}>
                            • {orgUserCounts[org.id] || 0} users
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280', flexShrink: 0 }}>
                            • {orgProjectCounts[org.id] || 0} projects
                          </Typography>
                          <Chip
                            icon={status.icon}
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: status.bgColor,
                              color: status.color,
                              fontWeight: 500,
                              height: '24px',
                              '& .MuiChip-icon': {
                                color: status.color
                              },
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
          
          <Box sx={{
            p: 2,
            backgroundColor: '#EFF6FF',
            borderRadius: '8px',
            border: '1px solid #BFDBFE',
            display: 'flex',
            gap: 1.5
          }}>
            <InfoIcon sx={{ color: '#3B82F6', fontSize: 20, mt: 0.2 }} />
            <Typography variant="body2" sx={{ color: '#1E40AF' }}>
              Adding another organization will allow you to manage multiple entities within Zavior.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );

  // Render Step 2: Basic Details
  const renderStep2 = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 3 }}>
        Step 1 of 3: Basic Details
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Organization Name
            <span style={{ color: 'red' }}> *</span>
          </Typography>
          <TextField
            fullWidth
            value={formData.organization_name}
            onChange={(e) => handleInputChange('organization_name', e.target.value)}
            error={!!errors.organization_name}
            helperText={errors.organization_name || 'e.g., Acme Corporation'}
            placeholder="e.g., Acme Corporation"
            variant="outlined"
            sx={textFieldSx}
          />
        </Box>

        {formData.country === 'Singapore' && (
          <OrganizationLookup
            organizationName={formData.organization_name}
            registeredEntity={formData.entity_name}
            contactEmail={userData?.email}
            onAcceptFill={handleAcceptOrganizationFill}
            disabled={false}
          />
        )}

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Registered Entity Name
          </Typography>
          <TextField
            fullWidth
            value={formData.entity_name}
            onChange={(e) => handleInputChange('entity_name', e.target.value)}
            helperText="e.g., Acme Corp Pte Ltd"
            placeholder="e.g., Acme Corp Pte Ltd"
            variant="outlined"
            sx={textFieldSx}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Registration/UEN Number
          </Typography>
          <TextField
            fullWidth
            value={formData.incorporation_number}
            onChange={(e) => handleInputChange('incorporation_number', e.target.value)}
            helperText="e.g., 202012345A"
            placeholder="e.g., 202012345A"
            variant="outlined"
            sx={textFieldSx}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Country
            <span style={{ color: 'red' }}> *</span>
          </Typography>
          <Autocomplete
            options={countriesName}
            getOptionLabel={(option) => option.name}
            value={countriesName.find(c => c.name === formData.country) || null}
            onChange={(event, newValue) => {
              handleInputChange('country', newValue?.name || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!errors.country}
                helperText={errors.country}
                placeholder="Select country"
                variant="outlined"
              />
            )}
            sx={textFieldSx}
          />
        </Box>

        {hasTechAdminAccess() && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_consultant}
                  onChange={(e) => handleInputChange('is_consultant', e.target.checked)}
                  sx={{
                    color: '#1D3DFC',
                    '&.Mui-checked': {
                      color: '#1D3DFC',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  This is a consultant organization
                </Typography>
              }
            />
          </Box>
        )}

      </Box>
    </Box>
  );

  // Render Step 3: Contact & Size
  const renderStep3 = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 3 }}>
        Step 2 of 3: Contact & Size
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Physical Address
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="123 Business Street&#10;#05-67, Business Tower&#10;Singapore 123456"
            variant="outlined"
            sx={textFieldSx}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Phone Number
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={formData.country_code}
                onChange={(e) => handleInputChange('country_code', e.target.value)}
                sx={{ ...textFieldSx }}
              >
                {countryCodes.map((cc) => (
                  <MenuItem key={cc.code} value={cc.callingCode}>
                    {cc.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value.replace(/\D/g, ''))}
              placeholder="6123 4567"
              variant="outlined"
              sx={textFieldSx}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Company Website
          </Typography>
          <TextField
            fullWidth
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            placeholder="https://acmecorp.com"
            variant="outlined"
            sx={textFieldSx}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Employee Size
          </Typography>
          <Autocomplete
            options={employeeSizeOptions}
            getOptionLabel={(option) => option.label}
            value={employeeSizeOptions.find(e => e.value === formData.employee_estimated) || null}
            onChange={(event, newValue) => {
              handleInputChange('employee_estimated', newValue?.value || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select range"
                variant="outlined"
              />
            )}
            sx={textFieldSx}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>
            Annual Turnover
          </Typography>
          <Autocomplete
            options={annualTurnoverOptions}
            getOptionLabel={(option) => option.label}
            value={annualTurnoverOptions.find(a => a.value === formData.annual_turnover) || null}
            onChange={(event, newValue) => {
              handleInputChange('annual_turnover', newValue?.value || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select range"
                variant="outlined"
              />
            )}
            sx={textFieldSx}
          />
        </Box>
      </Box>
    </Box>
  );

  // Render Step 4: Review & Create
  const renderStep4 = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 3 }}>
        Step 3 of 3: Review & Create
      </Typography>
      
      <Typography variant="body2" sx={{ color: '#374151', mb: 3 }}>
        Please review your organization information:
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {/* Organization Details */}
        <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '12px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                Organization Details
              </Typography>
              <IconButton
                size="small"
                onClick={() => setStep(2)}
                sx={{ color: '#6B7280' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>Name:</Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                  {formData.organization_name || '-'}
                </Typography>
              </Box>
              {formData.entity_name && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>Entity:</Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                    {formData.entity_name}
                  </Typography>
                </Box>
              )}
              {formData.incorporation_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>UEN:</Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                    {formData.incorporation_number}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>Country:</Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                  {formData.country || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Contact Information */}
        {(formData.address || formData.phone_number || formData.website) && (
          <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '12px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                  Contact Information
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setStep(3)}
                  sx={{ color: '#6B7280' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'grid', gap: 1 }}>
                {formData.address && (
                  <Box>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Address:</Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500, whiteSpace: 'pre-line' }}>
                      {formData.address}
                    </Typography>
                  </Box>
                )}
                {formData.phone_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Phone:</Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                      {formData.country_code} {formData.phone_number}
                    </Typography>
                  </Box>
                )}
                {formData.website && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Website:</Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                      {formData.website}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Organization Size */}
        {(formData.employee_estimated || formData.annual_turnover) && (
          <Card sx={{ border: '1px solid #E5E7EB', borderRadius: '12px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                  Organization Size
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setStep(3)}
                  sx={{ color: '#6B7280' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'grid', gap: 1 }}>
                {formData.employee_estimated && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Employees:</Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                      {formData.employee_estimated}
                    </Typography>
                  </Box>
                )}
                {formData.annual_turnover && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>Turnover:</Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                      {formData.annual_turnover}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Post-creation info and options */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Info message */}
        <Box sx={{
          p: 2,
          backgroundColor: '#EFF6FF',
          borderRadius: '8px',
          border: '1px solid #BFDBFE',
          display: 'flex',
          gap: 1.5
        }}>
          <InfoIcon sx={{ color: '#3B82F6', fontSize: 20, mt: 0.2 }} />
          <Typography variant="body2" sx={{ color: '#1E40AF' }}>
            You will be automatically added to this organization with your current role.
          </Typography>
        </Box>

        {/* Onboarding checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={continueToOnboarding}
              onChange={(e) => setContinueToOnboarding(e.target.checked)}
              sx={{
                color: '#1D3DFC',
                '&.Mui-checked': {
                  color: '#1D3DFC',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#374151' }}>
              Continue to onboarding after creation
            </Typography>
          }
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '85vh',
          width: '1200px'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A' }}>
            Add Organization
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: '#6B7280' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#FAFAFA' }}>
        <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: 2, p: 3, minHeight: '400px' }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#FAFAFA', borderTop: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: '#E2E8F0',
              color: '#64748B',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC'
              }
            }}
          >
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {step > 1 && step < 4 && (
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#6B7280',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Back
              </Button>
            )}
            {step === 1 && (
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#1D3DFC',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0E2BC0'
                  }
                }}
              >
                Add New Org
              </Button>
            )}
            {step > 1 && step < 4 && (
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#1D3DFC',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0E2BC0'
                  }
                }}
              >
                Next
              </Button>
            )}
            {step === 4 && (
              <Button
                onClick={handleCreate}
                variant="contained"
                disabled={loading}
                endIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#1D3DFC',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0E2BC0'
                  }
                }}
              >
                {loading ? 'Creating...' : 'Create & Next'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DialogOrganizationAdd;
